"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface LogissimoGradientBarProps {
  /**
   * Animation variant:
   * - "animated": Full animation (scale in + scrolling gradient)
   * - "subtle": Only scrolling gradient animation (no scale in)
   * - "static": No animation, just the gradient
   */
  variant?: "animated" | "subtle" | "static";
  /**
   * Height of the bar:
   * - "sm": 1px
   * - "md": 2px
   * - "lg": 4px
   */
  height?: "sm" | "md" | "lg";
  /**
   * Animation delay in seconds
   */
  delay?: number;
  /**
   * Additional CSS classes for positioning
   */
  className?: string;
}

const heightMap = {
  sm: "h-px",
  md: "h-0.5",
  lg: "h-1",
};

export function LogissimoGradientBar({
  variant = "animated",
  height = "sm",
  delay = 0,
  className,
}: LogissimoGradientBarProps) {
  const baseStyle = {
    background: "linear-gradient(90deg, #FFCC00, #00B2A9, #0066B3, #FFCC00)",
    backgroundSize: "200% 100%",
  };

  const heightClass = heightMap[height];

  // Static variant - no animation
  if (variant === "static") {
    return (
      <div
        className={cn(heightClass, "w-full", className)}
        style={baseStyle}
      />
    );
  }

  // Subtle variant - only scrolling animation, no scale in
  if (variant === "subtle") {
    return (
      <motion.div
        className={cn(heightClass, "w-full", className)}
        style={baseStyle}
        animate={{
          backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
        }}
        transition={{
          backgroundPosition: {
            duration: 5,
            repeat: Infinity,
            ease: "linear",
            delay,
          },
        }}
      />
    );
  }

  // Animated variant - full animation (scale in + scrolling)
  return (
    <motion.div
      className={cn(heightClass, "w-full origin-left", className)}
      style={baseStyle}
      initial={{ scaleX: 0 }}
      animate={{
        scaleX: 1,
        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
      }}
      transition={{
        scaleX: { duration: 1.2, delay, ease: "easeOut" },
        backgroundPosition: {
          duration: 5,
          repeat: Infinity,
          ease: "linear",
          delay: delay + 1.2,
        },
      }}
    />
  );
}
